import dayjs, { Dayjs } from "utils/dayjs"
import i18n from "intl/i18n"

import { ApiLinkEntity, ApiLinkRel } from "config/constants"
import { Guid, IApiLink } from "@/types"
import { ShippingOrderStatus, ShippingOrderType } from "./ShippingOrder"
import ShippingOrderCartTransport, { IGetShippingOrderCartTransportDto } from "./ShippingOrderCartTransport"
import Unit from "./Unit"
import ShippingOrderCartAsset, { IGetShippingOrderCartAssetDto } from "./ShippingOrderCartAsset"
import ShippingOrderCartManualPosition, { IGetShippingOrderCartManualPositionDto } from "./ShippingOrderCartManualPosition"
import RequestLocation from "./RequestLocation"

export enum ShippingOrdererCartState {
    incomplete = "incomplete",
    valid = "valid",
}

export interface ICreateShippingOrderCartDto {
    reservationId?: Guid
    type?: ShippingOrderType
    shippingOrderId?: Guid
    overrideOwnership?: boolean
}

export interface IUpdateShippingOrderCartDto {
    note?: string | null
    deliveryTermId?: Guid | null
    responsibleWarehouseId?: Guid | null
    departureDate?: string | null
    arrivalDate?: string | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    releaseStoreAllAssetsDate?: string | null
    deliveryNoteHeaderText?: string | null
    deliveryNoteFooterText?: string | null
    deliveryNotePrintFeatures?: boolean | null
}

export interface IGetShippingOrderCartDto {
    id: Guid
    number?: string | null
    reservationId: Guid
    shippingOrderId?: Guid | null
    cartState: ShippingOrdererCartState
    status: ShippingOrderStatus
    type?: ShippingOrderType | null
    note?: string | null
    deliveryTermId?: Guid | null
    responsibleWarehouseId?: Guid | null
    departureDate?: string | null
    arrivalDate?: string | null
    personInCharge?: string | null
    personInChargePhone?: string | null
    forwardToShipping: boolean
    assets: IGetShippingOrderCartAssetDto[]
    manualPositions: IGetShippingOrderCartManualPositionDto[]
    transports: IGetShippingOrderCartTransportDto[]
    releaseStoreAllAssetsDate?: string | null
    deliveryNoteHeaderText?: string | null
    deliveryNoteFooterText?: string | null
    deliveryNotePrintFeatures: boolean
    canExportTowerCraneLoadingList?: boolean
    links: IApiLink[]
}

export default class ShippingOrderCart {
    id: Guid
    number?: string
    reservationId: Guid
    shippingOrderId?: Guid
    cartState: ShippingOrdererCartState
    status: ShippingOrderStatus
    type?: ShippingOrderType
    note?: string
    deliveryTermId?: Guid
    responsibleWarehouseId?: Guid
    departureDate?: Dayjs
    arrivalDate?: Dayjs
    personInCharge?: string
    personInChargePhone?: string
    forwardToShipping: boolean
    assets: ShippingOrderCartAsset[]
    manualPositions: ShippingOrderCartManualPosition[]
    transports: ShippingOrderCartTransport[]
    releaseStoreAllAssetsDate?: Dayjs | null
    deliveryNoteHeaderText?: string
    deliveryNoteFooterText?: string
    deliveryNotePrintFeatures: boolean
    canExportTowerCraneLoadingList: boolean

    linkPublish?: IApiLink
    linkUpdate?: IApiLink
    linkTouch?: IApiLink
    linkDelete?: IApiLink
    linkChangeStatus?: IApiLink
    linkAddAssets?: IApiLink
    linkUpdateAsset?: IApiLink
    linkDeleteAssets?: IApiLink
    linkCreateManualPositions?: IApiLink
    linkUpdateManualPosition?: IApiLink
    linkDeleteManualPosition?: IApiLink
    linkCreateTransport?: IApiLink
    linkUpdateTransport?: IApiLink
    linkDeleteTransport?: IApiLink
    linkUpdateSegment?: IApiLink
    linkImportLoadingList?: IApiLink
    linkExportLoadingList?: IApiLink
    linkGetRequestLocations?: IApiLink
    linkCreateRequestLocation?: IApiLink
    linkGetPickingListReport?: IApiLink

    constructor(dto: IGetShippingOrderCartDto, sizeUnits: Unit[]) {
        this.id = dto.id
        this.number = dto.number || undefined
        this.reservationId = dto.reservationId
        this.shippingOrderId = dto.shippingOrderId || undefined
        this.cartState = dto.cartState
        this.status = dto.status
        this.type = dto.type || undefined
        this.note = dto.note || undefined
        this.deliveryTermId = dto.deliveryTermId || undefined
        this.responsibleWarehouseId = dto.responsibleWarehouseId || undefined
        this.departureDate =  dto.departureDate ? dayjs.utc(dto.departureDate) : undefined
        this.arrivalDate =  dto.arrivalDate ? dayjs.utc(dto.arrivalDate) : undefined
        this.personInCharge = dto.personInCharge || undefined
        this.personInChargePhone = dto.personInChargePhone || undefined
        this.forwardToShipping = dto.forwardToShipping
        this.assets = dto.assets.map((asset, index) => new ShippingOrderCartAsset(asset, index))
        this.manualPositions = dto.manualPositions.map(manualPosition => new ShippingOrderCartManualPosition(manualPosition))
        this.transports = dto.transports?.map((transportDto, index) => new ShippingOrderCartTransport({
            dto: transportDto,
            sizeUnits,
            sequence: index
        })) || []
        this.releaseStoreAllAssetsDate = dto.releaseStoreAllAssetsDate ? dayjs.utc(dto.releaseStoreAllAssetsDate) : undefined
        this.deliveryNoteHeaderText = dto.deliveryNoteHeaderText ?? undefined
        this.deliveryNoteFooterText = dto.deliveryNoteFooterText ?? undefined
        this.deliveryNotePrintFeatures = dto.deliveryNotePrintFeatures
        this.canExportTowerCraneLoadingList = !!dto.canExportTowerCraneLoadingList

        this.linkPublish = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrder && lnk.rel === ApiLinkRel.new)
        this.linkUpdate = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCart && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkTouch = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartLock && lnk.rel === ApiLinkRel.update)
        this.linkDelete = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCart && lnk.rel === ApiLinkRel.delete)
        this.linkChangeStatus = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartStatus && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkAddAssets = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartAsset && lnk.rel === ApiLinkRel.new)
        this.linkUpdateAsset = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartAsset && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkDeleteAssets = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartAsset && lnk.rel === ApiLinkRel.delete)
        this.linkCreateManualPositions = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartManualPosition && lnk.rel === ApiLinkRel.new)
        this.linkUpdateManualPosition = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartManualPosition && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkDeleteManualPosition = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartManualPosition && lnk.rel === ApiLinkRel.delete)
        this.linkCreateTransport = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartTransport && lnk.rel === ApiLinkRel.new)
        this.linkUpdateTransport = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartTransport && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkDeleteTransport = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartTransport && lnk.rel === ApiLinkRel.delete)
        this.linkUpdateSegment = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCartSegment && lnk.rel === ApiLinkRel.partialUpdate)
        this.linkImportLoadingList = dto.links.find(lnk => lnk.entity === ApiLinkEntity.shippingOrderCart && lnk.rel === ApiLinkRel.import)
        this.linkExportLoadingList = dto.links.find(lnk => lnk.entity === ApiLinkEntity.loadingListReport && lnk.rel === ApiLinkRel.self)
        this.linkGetRequestLocations = dto.links.find(lnk => lnk.entity === ApiLinkEntity.requestLocation && lnk.rel === ApiLinkRel.all)
        this.linkCreateRequestLocation = dto.links.find(lnk => lnk.entity === ApiLinkEntity.requestLocation && lnk.rel === ApiLinkRel.new)
        this.linkGetPickingListReport = dto.links.find(lnk => lnk.entity === ApiLinkEntity.pickingListReport && lnk.rel === ApiLinkRel.self)
    }

    get isPushedToStock(): boolean {
        return this.status === ShippingOrderStatus.pushedToStock
            || this.status === ShippingOrderStatus.inProgress
            || this.status === ShippingOrderStatus.completed
    }

    get isShippingStarted(): boolean {
        return this.status === ShippingOrderStatus.inProgress
            || this.status === ShippingOrderStatus.completed
    }

    get isReadyToReleaseOrStore(): boolean {
        return !this.#hasUnassignedAssets
            && !this.#hasUnassignedManualPositions
            && !this.#hasEmptyTransport
            && !!this.linkUpdate
    }

    get canReject(): boolean {
        return !this.isPushedToStock
    }

    get #hasUnassignedAssets(): boolean {
        return this.assets.some(asset => !asset.isInTransport)
    }

    get #hasUnassignedManualPositions(): boolean {
        return this.manualPositions.some(mp => !mp.isInTransport)
    }

    get #hasEmptyTransport(): boolean {
        return this.transports.some(transport => {
            return !this.assets.some(asset => asset.transportId === transport.id)
                && !this.manualPositions.some(mp => mp.transportId === transport.id)
        })
    }

    get isLoadingListReportDraft(): boolean {
        for (let asset of this.assets) {
            if (!asset.transportId) {
                return true
            }
        }
        return false
    }

    printDate() {
        if (!this.departureDate) return
        if (!this.arrivalDate) return this.departureDate.format(i18n.t('config.dateFormat'))
        return `${this.departureDate.format(i18n.t('config.dateFormat'))} – ${this.arrivalDate.format(i18n.t('config.dateFormat'))}`
    }

    getDepartureLocation(requestLocations: RequestLocation[] | null) {
        // temporary solution - take first asset loading location as SO loading location
        if (!requestLocations) return
        const asset = this.assets[0]
        if (!asset) return
        return requestLocations.find(requestLocation => requestLocation.id === asset.departureLocationId)
    }

    getArrivalLocation(requestLocations: RequestLocation[] | null) {
        // temporary solution - take first asset unloading location as SO unloading location
        if (!requestLocations) return
        const asset = this.assets[0]
        if (!asset) return
        return requestLocations.find(requestLocation => requestLocation.id === asset.arrivalLocationId)
    }

    get hasAssetsWithoutDepartureOrArrival() {
        return this.assets.some(asset => !asset.departureLocationId || !asset.arrivalLocationId)
    }

    getTransportSegmentItems({ transportId, segmentId } : { transportId: Guid, segmentId: Guid }) {
        const transport = this.transports.find(t => t.id === transportId)
        const segment = transport?.segments.find(s => s.id === segmentId)
        return {
            assets: this.assets.filter(a =>
                a.transportId === transportId
                && a.departureLocationId === segment?.departureLocationId
                && a.arrivalLocationId === segment?.arrivalLocationId
            ),
            manualPositions: this.manualPositions.filter(mp =>
                mp.transportId === transportId
                && mp.departureLocationId === segment?.departureLocationId
                && mp.arrivalLocationId === segment?.arrivalLocationId
            ),
            segment,
        }
    }
}
